import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { BookCover } from '@/components/books/BookCover';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * BookCoverUploader — the 160×230 cover card at the top of the admin book
 * editor's left column per DESIGN.md Artboard 4. When a real
 * `book.bookCover` URL is set we render the image; otherwise we fall back
 * to the same deterministic generative placeholder used on BookCard so the
 * layout never jumps.
 *
 * Two affordances, both admin-gated server-side:
 *   - Replace → opens a hidden `<input type="file">` and POSTs the chosen
 *     file via `booksService.uploadCover` (multipart).
 *   - Remove → DELETEs the stored file and clears `book_cover`.
 *
 * The component is controlled — the parent owns the current book and we
 * hand it the refreshed payload after every successful mutation via
 * `onBookChange`. That way the meta form, label rail, and activity log can
 * all react to the new state from one source.
 */
export interface BookCoverUploaderProps {
  book: BookView;
  onBookChange: (book: BookView) => void;
  className?: string;
}

/**
 * Client-side guardrails before the upload — the server still validates
 * mimetype / size, but a quick local check saves a round-trip and gives
 * a better error message. Limits mirror the server's multipart config.
 */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export function BookCoverUploader({ book, onBookChange, className }: BookCoverUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const busy = uploading || removing;

  const handlePick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Cover must be a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('Cover files must be 5MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const updated = await booksService.uploadCover(book.id, file);
      onBookChange(updated);
      toast.success('Cover updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not upload the cover.');
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires `onChange`.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (busy || !book.bookCover) return;
    setRemoving(true);
    try {
      const updated = await booksService.deleteCover(book.id);
      onBookChange(updated);
      toast.success('Cover removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the cover.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className={cn('rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm', className)}>
      <div className="or-kicker text-or-slate-500">Cover image</div>
      <div className="mt-3 flex items-start gap-5">
        <div className="shrink-0">
          {book.bookCover ? (
            <img
              src={book.bookCover}
              alt={`${book.title} cover`}
              width={160}
              height={230}
              className="h-[230px] w-[160px] rounded-xl object-cover object-top shadow-or-card"
            />
          ) : (
            <BookCover
              title={book.title}
              author={book.author}
              seed={book.id}
              w={160}
              h={230}
              className="h-[230px] w-[160px]"
            />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-[12px] leading-relaxed text-or-slate-600">
            {book.bookCover
              ? 'Replace the current cover or remove it to fall back to the generated placeholder.'
              : 'Upload a PNG, JPEG, WEBP, or GIF. Max 5MB.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={handlePick} disabled={busy}>
              {uploading ? 'Uploading…' : book.bookCover ? 'Replace' : 'Upload cover'}
            </Button>
            {book.bookCover ? (
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemove}
                disabled={busy}
                title="Clear the uploaded cover"
              >
                {removing ? 'Removing…' : 'Remove'}
              </Button>
            ) : null}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </div>
      </div>
    </div>
  );
}
